
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import { Category } from "@/types";
import { Upload } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";

// Form schema
const categorySchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  image: z.string().optional(),
  parentId: z.string().optional(),
});

type CategoryFormValues = z.infer<typeof categorySchema>;

interface CategoryFormProps {
  activeTab: string;
  editingId: string | null;
  categories: Category[];
  onSubmit: (values: CategoryFormValues) => void;
  onCancel: () => void;
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  activeTab,
  editingId,
  categories,
  onSubmit,
  onCancel
}) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { toast } = useToast();
  
  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: "",
      image: "",
      parentId: "",
    },
  });

  // Carregar dados para edição
  useEffect(() => {
    if (editingId) {
      const category = activeTab === "categories"
        ? categories.find(cat => cat.id === editingId)
        : categories.find(cat => cat.id === editingId);

      if (category) {
        form.reset({
          name: category.name,
          image: category.image,
          parentId: activeTab === "subcategories" ? category.parentId : undefined,
        });
      }
    } else {
      form.reset({
        name: "",
        image: "",
        parentId: "",
      });
    }
  }, [editingId, categories, activeTab, form]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Handle image upload
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);

      // Clean up previous preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      // Creating a preview URL for the image (for display only)
      const newPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(newPreviewUrl);

      toast({
        title: "Imagem selecionada",
        description: `${file.name} (${Math.round(file.size / 1024)}KB)`,
      });
    }
  };

  // Upload image to Supabase Storage
  const uploadImageToStorage = async (file: File): Promise<string | null> => {
    let filePath = 'unknown'; // Declare filePath at function start

    try {
      setUploading(true);

      // Validate file type (images only)
      if (!file.type.match('image.*')) {
        toast({
          title: "Erro",
          description: "Por favor, selecione apenas ficheiros de imagem",
          variant: "destructive",
        });
        return null;
      }

      // Validate file size (3MB limit for categories)
      if (file.size > 3 * 1024 * 1024) {
        toast({
          title: "Erro",
          description: "O ficheiro é muito grande. Limite de 3MB",
          variant: "destructive",
        });
        return null;
      }

      const fileExt = file.name.split('.').pop();
      if (!fileExt) {
        toast({
          title: "Erro",
          description: "O ficheiro deve ter uma extensão válida",
          variant: "destructive",
        });
        return null;
      }
      const fileName = `${uuidv4()}.${fileExt}`;
      filePath = fileName; // Just the filename since we're specifying the bucket

      const { error: uploadError, data } = await supabase.storage
        .from('categories')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('categories')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (error: any) {
      console.error('Erro ao fazer upload da imagem:', {
        bucket: 'categories',
        filePath,
        fileSize: file.size,
        fileType: file.type,
        fileName: file.name,
        error: error.message,
        errorCode: error.code,
        statusCode: error.statusCode,
        fullError: error
      });

      // Provide more specific error messages based on different error types
      let errorMessage = "Erro de comunicação com o servidor. Tente novamente.";

      if (error.message?.includes('Invalid MIME type') || error.message?.includes('mime')) {
        errorMessage = "Tipo de ficheiro não suportado. Use apenas imagens (JPG, PNG, GIF, WebP)";
      } else if (error.message?.includes('File size too large') || error.message?.includes('size')) {
        errorMessage = "Ficheiro muito grande. Limite máximo: 3MB";
      } else if (error.message?.includes('Bucket not found') || error.message?.includes('bucket')) {
        errorMessage = "Erro de configuração do armazenamento. Contacte o suporte";
      } else if (error.message?.includes('permission') || error.message?.includes('policy') || error.statusCode === 403) {
        errorMessage = "Sem permissões para fazer upload. Verifique se está autenticado como administrador";
      } else if (error.message?.includes('row-level security') || error.message?.includes('RLS')) {
        errorMessage = "Erro de segurança. Verifique as suas permissões de administrador";
      } else if (error.statusCode === 400) {
        errorMessage = "Pedido inválido. Verifique o ficheiro e tente novamente";
      } else if (error.statusCode === 401) {
        errorMessage = "Não autorizado. Faça login novamente";
      } else if (error.statusCode === 500) {
        errorMessage = "Erro interno do servidor. Tente novamente mais tarde";
      }

      toast({
        title: "Erro no upload",
        description: errorMessage,
        variant: "destructive",
      });

      return null;
    } finally {
      setUploading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (values: CategoryFormValues) => {
    try {
      // Se tiver um ficheiro de imagem para upload
      if (imageFile) {
        const imageUrl = await uploadImageToStorage(imageFile);
        if (imageUrl) {
          values.image = imageUrl;
        } else {
          // If image upload failed, don't proceed with form submission
          return;
        }
      }

      // Chamar a função onSubmit do componente pai com os valores atualizados
      onSubmit(values);

      // Reset image file and preview after successful submission
      setImageFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: "Erro",
        description: "Erro de comunicação com o servidor. Tente novamente.",
        variant: "destructive"
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="mb-6">
        <div className="bg-brand-dark p-4 rounded-md">
          <h3 className="text-lg font-semibold mb-4">
            {editingId ? "Editar " : "Adicionar Nova "}
            {activeTab === "categories" ? "Categoria" : "Subcategoria"}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome da {activeTab === "categories" ? "categoria" : "subcategoria"}*</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="bg-brand-darker text-white"
                      placeholder={`Digite o nome da ${activeTab === "categories" ? "categoria" : "subcategoria"}`}
                    />
                  </FormControl>
                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />
            
            {activeTab === "subcategories" && (
              <FormField
                control={form.control}
                name="parentId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoria principal*</FormLabel>
                    <Select 
                      value={field.value} 
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger className="bg-brand-darker text-white">
                        <SelectValue placeholder="Selecione uma categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(category => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage className="text-red-400" />
                  </FormItem>
                )}
              />
            )}
            
            <div>
              <Label htmlFor={`${activeTab}-image-upload`}>Imagem da {activeTab === "categories" ? "categoria" : "subcategoria"}</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button
                  type="button"
                  variant="outline"
                  disabled={uploading}
                  className="bg-transparent border-gray-600 text-white hover:bg-gray-800"
                  onClick={() => document.getElementById(`${activeTab}-image-upload`)?.click()}
                >
                  <Upload size={16} className="mr-2" />
                  {uploading ? "Carregando..." : "Selecionar Imagem"}
                </Button>
                <Input
                  id={`${activeTab}-image-upload`}
                  type="file"
                  className="hidden"
                  onChange={handleImageUpload}
                  accept="image/*"
                />
                {(form.getValues("image") || previewUrl) && (
                  <div className="h-10 w-10 rounded overflow-hidden">
                    <img
                      src={previewUrl || form.getValues("image")}
                      alt="Preview"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        console.warn('Image preview failed to load:', e.currentTarget.src);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              type="submit"
              className="bg-brand-red text-white hover:bg-brand-red/90"
              disabled={uploading}
            >
              {uploading ? "Enviando..." : editingId ? "Guardar Alterações" : `Adicionar ${activeTab === "categories" ? "Categoria" : "Subcategoria"}`}
            </Button>
            <Button
              type="button"
              onClick={onCancel}
              variant="outline"
              className="bg-transparent border-gray-600 text-white hover:bg-gray-800"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default CategoryForm;
